import React, { useState } from 'react';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { getStatusConfig, getAllStatuses, mapLegacyStatus } from '../utils/bookingStatus';
import DataFetchState from '../components/ui/DataFetchState';
import DashboardSkeleton from '../components/ui/DashboardSkeleton';
import AnimatedEmptyState from '../components/ui/AnimatedEmptyState';
import { 
  Calendar, 
  Filter, 
  Search, 
  Clock, 
  CheckCircle, 
  XCircle,
  AlertTriangle,
  ArrowRight,
  ChevronDown,
  MessageCircle,
  Star
} from 'lucide-react';
import { Link } from 'react-router-dom';

const OwnerBookings = () => {
  const { bookings, userDataLoading, userDataError, refreshUserData } = useData();
  const { user } = useAuth();
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [dateRange, setDateRange] = useState('all');
  const [expandedId, setExpandedId] = useState(null);

  const userBookings = bookings.filter(booking => booking.ownerId === (user?.uid || user?.id));

  const filteredBookings = userBookings.filter(booking => {
    // Normalize status for filtering
    const normalizedStatus = mapLegacyStatus(booking.status);
    
    // Status filter
    if (filter !== 'all' && normalizedStatus !== filter) return false;

    // Search filter
    const searchMatch = booking.caregiver?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       booking.pet?.name.toLowerCase().includes(searchTerm.toLowerCase());
    if (searchTerm && !searchMatch) return false;

    // Date range filter
    if (dateRange !== 'all') {
      const bookingDate = new Date(booking.start);
      const now = new Date();
      switch (dateRange) {
        case 'upcoming':
          return bookingDate > now;
        case 'past':
          return bookingDate < now;
        case 'today':
          return bookingDate.toDateString() === now.toDateString();
        default:
          return true;
      }
    }

    return true;
  });

  const stats = {
    all: userBookings.length,
    requested: userBookings.filter(b => mapLegacyStatus(b.status) === 'requested').length,
    confirmed: userBookings.filter(b => mapLegacyStatus(b.status) === 'confirmed').length,
    active: userBookings.filter(b => mapLegacyStatus(b.status) === 'active').length,
    completed: userBookings.filter(b => mapLegacyStatus(b.status) === 'completed').length,
    cancelled: userBookings.filter(b => mapLegacyStatus(b.status) === 'cancelled').length,
    declined: userBookings.filter(b => mapLegacyStatus(b.status) === 'declined').length
  };

  const statusColors = {
    requested: 'bg-yellow-500',
    accepted: 'bg-green-500',
    in_progress: 'bg-blue-500',
    completed: 'bg-[var(--pf-accent)]',
    cancelled: 'bg-red-500'
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  return (
    <div className="pf-page-wrap pb-32 lg:pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="pf-display text-3xl lg:text-4xl text-[var(--pf-ink)] mb-2">
            My Bookings
          </h1>
          <p className="text-[var(--pf-ink-muted)]">
            Track and manage all your pet care bookings
          </p>
        </div>

        {userDataLoading ? (
          <DashboardSkeleton statCount={6} />
        ) : userDataError ? (
          <DataFetchState
            error={userDataError}
            onRetry={refreshUserData}
            errorTitle="Couldn't load your bookings"
            compact
            className="mb-8"
          />
        ) : (
        <>
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {[
            { key: 'all', label: 'All Bookings', icon: Calendar },
            ...getAllStatuses().filter(s => s.value !== 'all').map(s => ({
              key: s.value,
              label: s.label,
              icon: s.value === 'requested' ? Clock :
                    s.value === 'confirmed' ? CheckCircle :
                    s.value === 'active' ? Clock :
                    s.value === 'completed' ? Star :
                    s.value === 'cancelled' ? XCircle :
                    AlertTriangle
            }))
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`pf-organic-card p-4 text-left transition-all hover:-translate-y-1 ${
                filter === key ? 'ring-2 ring-[var(--pf-accent)] ring-offset-2' : ''
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  key === 'all' ? 'bg-[var(--pf-accent-soft)] text-[var(--pf-accent)]' :
                  key === 'requested' ? 'bg-yellow-100 text-yellow-600' :
                  key === 'confirmed' ? 'bg-green-100 text-green-600' :
                  key === 'active' ? 'bg-blue-100 text-blue-600' :
                  key === 'completed' ? 'bg-[var(--pf-accent-soft)] text-[var(--pf-accent)]' :
                  'bg-red-100 text-red-600'
                }`}>
                  <Icon className="w-5 h-5" />
                </div>
                <span className="text-2xl font-bold text-gray-900">{stats[key] || 0}</span>
              </div>
              <p className="text-sm text-gray-600">{label}</p>
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="card mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative input-icon-wrap">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-input"
                  placeholder="Search by caregiver or pet name..."
                />
              </div>
            </div>

            {/* Date Range */}
            <div className="w-full lg:w-64">
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="form-input"
              >
                <option value="all">All Dates</option>
                <option value="upcoming">Upcoming</option>
                <option value="past">Past</option>
                <option value="today">Today</option>
              </select>
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="btn-secondary inline-flex items-center"
            >
              <Filter className="w-5 h-5 mr-2" />
              More Filters
              <ChevronDown className={`w-5 h-5 ml-2 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {/* Extended Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Additional filters can be added here */}
            </div>
          )}
        </div>

        {/* Bookings List */}
        <div className="space-y-4">
          {filteredBookings.length > 0 ? (
            filteredBookings.map((booking) => (
              <div key={booking.id} className="card hover:shadow-lg transition-all">
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  {/* Caregiver Info */}
                  <div className="flex items-center gap-4 flex-1">
                    <img
                      src={booking.caregiver?.avatar}
                      alt={booking.caregiver?.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <h3 className="font-semibold text-gray-900">{booking.caregiver?.name}</h3>
                      <p className="text-sm text-gray-600">{booking.pet?.name}</p>
                    </div>
                  </div>

                  {/* Booking Details */}
                  <div className="flex flex-wrap gap-4 lg:gap-8">
                    {/* Date & Time */}
                    <div>
                      <p className="text-sm text-gray-500">Date</p>
                      <p className="font-medium">{formatDate(booking.start)}</p>
                      <p className="text-sm text-gray-500">{formatTime(booking.start)}</p>
                    </div>

                    {/* Duration */}
                    <div>
                      <p className="text-sm text-gray-500">Duration</p>
                      <p className="font-medium">{booking.durationHours} hours</p>
                    </div>

                    {/* Amount */}
                    <div>
                      <p className="text-sm text-gray-500">Amount</p>
                      <p className="font-medium">₹{booking.escrowAmount}</p>
                    </div>

                    {/* Status */}
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Status</p>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusConfig(booking.status).color}`}>
                        {getStatusConfig(booking.status).icon} {getStatusConfig(booking.status).label}
                      </span>
                      <p className="text-xs text-gray-600 mt-1">
                        {getStatusConfig(booking.status).descriptionForOwner}
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/owner-messages?booking=${booking.id}`}
                      className="btn-outline p-2 rounded-lg"
                    >
                      <MessageCircle className="w-5 h-5" />
                    </Link>
                    <button
                      onClick={() => setExpandedId(expandedId === booking.id ? null : booking.id)}
                      className="btn-primary px-4 py-2 inline-flex items-center gap-1"
                      aria-expanded={expandedId === booking.id}
                    >
                      {expandedId === booking.id ? 'Hide Details' : 'View Details'}
                      <ChevronDown className={`w-4 h-4 transition-transform ${expandedId === booking.id ? 'rotate-180' : ''}`} />
                    </button>
                  </div>
                </div>

                {expandedId === booking.id && (
                  <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Service</p>
                      <p className="font-medium text-gray-900">{booking.serviceType || 'Pet Care'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Location</p>
                      <p className="font-medium text-gray-900">
                        {booking.location || booking.caregiver?.location || 'Not specified'}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Pet</p>
                      <p className="font-medium text-gray-900">
                        {booking.pet?.name || 'N/A'}
                        {booking.pet?.breed ? ` · ${booking.pet.breed}` : ''}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Ends</p>
                      <p className="font-medium text-gray-900">
                        {formatDate(booking.end)} {formatTime(booking.end)}
                      </p>
                    </div>
                    <div className="sm:col-span-2">
                      <p className="text-gray-500">Notes</p>
                      <p className="font-medium text-gray-900">
                        {booking.notes || 'No special requirements'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))
          ) : (
            <AnimatedEmptyState
              icon="loader-tail-wag"
              title="No bookings found"
              description={
                searchTerm || filter !== 'all'
                  ? 'Try adjusting your search or filter criteria'
                  : 'Start by finding a caregiver for your pet'
              }
              action={
                !searchTerm && filter === 'all' ? (
                  <Link to="/search" className="pf-btn-primary">
                    Find caregivers
                  </Link>
                ) : null
              }
            />
          )}
        </div>

        {/* Help Section */}
        {userBookings.length > 0 && (
          <div className="mt-12">
            <div className="pf-organic-card bg-[var(--pf-accent-soft)] border-[color-mix(in_srgb,var(--pf-accent)_20%,var(--pf-border))]">
              <div className="flex items-start gap-6">
                <div className="w-12 h-12 rounded-xl bg-[var(--pf-accent)] flex items-center justify-center shrink-0">
                  <AlertTriangle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Need Help?</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="font-medium text-gray-900 mb-1">Booking Issues</p>
                      <p className="text-gray-600">Contact our support team for any booking-related questions or issues.</p>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 mb-1">Emergency Support</p>
                      <p className="text-gray-600">24/7 emergency support available for urgent pet care situations.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
};

export default OwnerBookings;